<script setup lang="ts">
import { computed } from 'vue';

interface TestimonialItemBlok {
    _uid: string;
    /** Plan / Storyblok schema */
    text?: string;
    name?: string;
    rating?: number | string;
    /** Legacy field names */
    quote?: string;
    author?: string;
}

const props = defineProps<{ blok: TestimonialItemBlok }>();

const bodyText = computed(() => props.blok.text ?? props.blok.quote ?? '');

const authorName = computed(() => props.blok.name ?? props.blok.author ?? '');

const starCount = computed(() => {
    const raw = props.blok.rating;
    const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
    if (! Number.isFinite(n) || n < 1) {
        return 5;
    }

    return Math.min(5, Math.max(1, Math.round(n)));
});
</script>

<template>
    <div v-editable="blok" class="flex flex-col rounded-3xl bg-white px-3 py-6 xl:px-11 xl:py-8">
        <div class="mb-3 flex gap-1 xl:mb-6" aria-hidden="true">
            <span v-for="n in starCount" :key="n">
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M11.2827 4.14887C11.5131 3.68193 11.6284 3.44846 11.7848 3.37387C11.9209 3.30897 12.0791 3.30897 12.2152 3.37387C12.3717 3.44846 12.4869 3.68193 12.7174 4.14887L14.9041 8.57884C14.9721 8.71669 15.0061 8.78562 15.0558 8.83913C15.0999 8.88651 15.1527 8.92491 15.2113 8.95218C15.2776 8.98298 15.3536 8.9941 15.5057 9.01633L20.397 9.73126C20.9121 9.80655 21.1696 9.84419 21.2888 9.96999C21.3925 10.0794 21.4412 10.2299 21.4215 10.3793C21.3988 10.5511 21.2124 10.7327 20.8395 11.0959L17.3014 14.542C17.1912 14.6494 17.136 14.7031 17.1004 14.767C17.0689 14.8236 17.0487 14.8858 17.0409 14.9501C17.0321 15.0227 17.0451 15.0986 17.0711 15.2503L17.906 20.1177C17.994 20.631 18.038 20.8877 17.9553 21.0401C17.8833 21.1726 17.7554 21.2655 17.6071 21.293C17.4366 21.3246 17.2061 21.2034 16.7451 20.961L12.3724 18.6614C12.2361 18.5897 12.168 18.5539 12.0962 18.5398C12.0327 18.5274 11.9673 18.5274 11.9038 18.5398C11.832 18.5539 11.7639 18.5897 11.6277 18.6614L7.25492 20.961C6.79392 21.2034 6.56341 21.3246 6.39297 21.293C6.24468 21.2655 6.11672 21.1726 6.04474 21.0401C5.962 20.8877 6.00603 20.631 6.09407 20.1177L6.92889 15.2503C6.95491 15.0986 6.96793 15.0227 6.95912 14.9501C6.95132 14.8858 6.93111 14.8236 6.89961 14.767C6.86402 14.7031 6.80888 14.6494 6.69859 14.542L3.16056 11.0959C2.78766 10.7327 2.60121 10.5511 2.57853 10.3793C2.55879 10.2299 2.60755 10.0794 2.71125 9.96999C2.83044 9.84419 3.08797 9.80655 3.60304 9.73126L8.49431 9.01633C8.64642 8.9941 8.72248 8.98298 8.78872 8.95218C8.84736 8.92491 8.90016 8.88651 8.94419 8.83913C8.99391 8.78562 9.02793 8.71669 9.09597 8.57884L11.2827 4.14887Z"
                        fill="#F5A77D"
                    />
                </svg>
            </span>
        </div>
        <p class="mb-2 grow text-xl text-[#335454] xl:text-base">
            {{ bodyText }}
        </p>
        <div class="text-2xl font-bold text-[#335454]">{{ authorName }}</div>
    </div>
</template>
